import { Input } from '@/components/ui/input';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface EditableTitleProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxLength?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  onChange,
  placeholder = 'Enter title...',
  className,
  disabled = false,
  maxLength = 100,
  onBlur,
  onFocus,
  autoFocus = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (autoFocus) {
      setIsEditing(true);
    }
  }, [autoFocus]);

  const handleStartEdit = useCallback(() => {
    if (!disabled) {
      setIsEditing(true);
      onFocus?.();
    }
  }, [disabled, onFocus]);

  const handleSave = useCallback(() => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== value) {
      onChange(trimmedValue || placeholder);
    }
    setIsEditing(false);
    onBlur?.();
  }, [editValue, value, onChange, placeholder, onBlur]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setIsEditing(false);
    onBlur?.();
  }, [value, onBlur]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }, [handleSave, handleCancel]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (maxLength && newValue.length <= maxLength) {
      setEditValue(newValue);
    }
  }, [maxLength]);

  const handleInputBlur = useCallback(() => {
    // Small delay to allow click events to process
    setTimeout(() => {
      handleSave();
    }, 100);
  }, [handleSave]);

  if (isEditing) {
    return (
      <div className={`relative ${className || ''}`}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          className="font-semibold text-lg border-primary focus:ring-2 focus:ring-primary/20"
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
          <button
            type="button"
            onClick={handleSave}
            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
            title="Save"
          >
            <CheckIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
            title="Cancel"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        group relative cursor-pointer rounded px-2 py-1 transition-colors
        ${disabled
          ? 'text-muted-foreground cursor-not-allowed'
          : 'hover:bg-muted/50 text-foreground'
        }
        ${className || ''}
      `}
      onClick={handleStartEdit}
      title={disabled ? undefined : "Click to edit"}
    >
      <span className="font-semibold text-lg select-none">
        {value || placeholder}
      </span>
      {!disabled && (
        <EditIcon className="absolute right-1 top-1/2 transform -translate-y-1/2 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
      )}
    </div>
  );
};

// Icons
const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20,6 9,17 4,12" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const EditIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
); 